import React, { useEffect, useRef, useState } from "react";
import { Outlet, Link, useLocation } from "react-router";
import {
  ChevronDownIcon,
  MoonIcon,
  SunIcon,
} from "@heroicons/react/24/outline";
import { useDarkMode } from "../hooks/useDarkMode";
import tools, { TOOL_ICON_COLOR_CLASSES } from "../data/tools";

function Layout() {
  const location = useLocation();
  const [isDarkMode, toggleDarkMode] = useDarkMode();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  // Any navigation closes the menu, including the browser's Back and
  // Forward buttons, which none of the listeners below can see. Resetting
  // state during render when an input changed is React's documented
  // pattern for this ; an effect would paint the open panel once on the
  // new page first.
  const [menuPath, setMenuPath] = useState(location.pathname);
  if (menuPath !== location.pathname) {
    setMenuPath(location.pathname);
    setMenuOpen(false);
  }

  // While the panel is open : a press or a focus move outside it closes
  // it, and Escape closes it and returns focus to the button. Document
  // listeners rather than onBlur, because the removed 2026-08-23 menu
  // closed on blur behind a timer, which raced the click on an entry, and
  // Safari does not focus a link it clicks at all.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeIfOutside = (event) => {
      if (!menuRef.current.contains(event.target)) setMenuOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        triggerRef.current.focus();
      }
    };
    document.addEventListener("pointerdown", closeIfOutside);
    document.addEventListener("focusin", closeIfOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeIfOutside);
      document.removeEventListener("focusin", closeIfOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menuOpen]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300 flex flex-col">
      {/* Navigation */}
      <nav className="bg-white dark:bg-gray-800 shadow-xs border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* relative : the Tools panel is positioned against this row,
              so it spans the content width on a phone and sits under the
              right-hand edge from sm up. */}
          <div className="relative flex justify-between h-16">
            {/* Logo, which is also the way back to the tool index */}
            <div className="shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <img
                  className="h-8 w-auto"
                  src="https://cdn.trustico.com/logo/trustico-logo-1000x256.png"
                  alt="Trustico®"
                />
              </Link>
            </div>

            {/*
              TOOLS MENU, RIGHT-ALIGNED (operator ruling 2026-10-01,
              reversing the one-link header of 2026-08-23) : a Tools
              button opens a panel listing every entry in
              src/client/data/tools.js, API Documentation included, and
              the dark-mode toggle stays beside it.

              The panel renders the home page's own list. The tools
              dropdown removed on 2026-08-23 was a second, hand-written
              list, and it was the copy that went stale : the Issuance
              Tool was missing from it for weeks and nothing noticed.

              A disclosure (a button with aria-expanded, then a list of
              real links) rather than a <select> or role="menu". A
              <select> that navigates on change sends a keyboard user to
              the first option they arrow onto, and role="menu" promises
              arrow-key handling that site navigation does not need (the
              WAI-ARIA disclosure navigation pattern). The current page is
              marked inside the panel : the old link's underline sat on
              the nav's own border and was barely visible.
            */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div ref={menuRef}>
                <button
                  ref={triggerRef}
                  type="button"
                  aria-expanded={menuOpen}
                  aria-controls="site-menu"
                  onClick={() => setMenuOpen((open) => !open)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md font-medium text-muted hover:text-body aria-expanded:text-heading transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  Tools
                  <ChevronDownIcon
                    className={`w-4 h-4 ${menuOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {/* Always in the DOM and hidden when closed, so
                    aria-controls always points at an element ; Tailwind's
                    preflight makes [hidden] display:none over the
                    positioning classes. */}
                <ul
                  id="site-menu"
                  hidden={!menuOpen}
                  className="card shadow-lg absolute right-0 top-full mt-2 z-40 w-full sm:w-96 py-2"
                >
                  {tools.map((tool) => {
                    const current = location.pathname === tool.href;
                    return (
                      <li key={tool.href}>
                        <Link
                          to={tool.href}
                          aria-current={current ? "page" : undefined}
                          onClick={() => setMenuOpen(false)}
                          className={`flex items-center gap-3 px-4 py-2 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${
                            current
                              ? "bg-gray-100 dark:bg-gray-700 font-semibold text-heading"
                              : "text-body hover:text-heading hover:bg-gray-50 dark:hover:bg-gray-700/50"
                          }`}
                        >
                          <tool.icon
                            className={`w-5 h-5 shrink-0 ${TOOL_ICON_COLOR_CLASSES[tool.color] || "text-link"}`}
                          />
                          {tool.title}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-md text-muted hover:text-body focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500"
                aria-label="Toggle dark mode"
              >
                {isDarkMode ? (
                  <SunIcon className="h-6 w-6" />
                ) : (
                  <MoonIcon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <p className="text-muted">
              © {new Date().getFullYear()} Trustico® Online Security - All
              Rights Reserved
            </p>
            <a
              href="https://www.trustico.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted hover:text-body transition-colors mt-2 sm:mt-0"
            >
              Visit Trustico® Online Security
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
